import React from 'react';
import { SectionPanel } from 'ui';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { SavedReports } from 'app/area/shared/saved-reports/form/saved-reports';
import { SavedReportCategory } from 'app/area/shared/saved-reports/constants';
import { SavedReportsValidation, SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { endpoints } from 'app/area/dashboard-administration/common/endpoints';
import { AddSavedReportsHandler, OnUpdateSavedReport, UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';
import { CompanyReportAdminTranslation } from '../../constants';
import { createDefaultSavedReport } from '../utils/create-default-saved-report';
import { validateSavedReport } from '../utils/validate-saved-report';
import { CompanyReportSectionType } from '../metroline/types';
import { SavedReportCompanyReport } from '../types-store';
import { CompanyReportSectionProps } from '../types';
import { CompanyReportDynamicPeriods } from './company-report-dynamic-periods';
import { SavedReportForm } from './saved-report-form';

interface CompanyReportSavedReportsProps extends WithMetroLineWrappedComponentProps<CompanyReportSectionType> {
  savedReports: SavedReportCompanyReport[];
  currentSavedReportId: number;
  currentSavedReport: SavedReportCompanyReport;
  currentSavedReportOnlyIdMode: boolean;
  currentValidation: SavedReportValidation<SavedReportCompanyReport>;
  displayDisabled: boolean;

  addSavedReports: AddSavedReportsHandler<SavedReportCompanyReport>;
  updateSavedReport: UpdateSavedReportHandler<SavedReportCompanyReport>;
  deleteSavedReport: (savedReportId: number) => void;
  deleteAllSavedReports: () => void;

  fetchingAddSavedReports: boolean;

  selectCurrent: (savedReport: SavedReportCompanyReport, onlyIdMode: boolean) => void;
  updateCurrent: (savedReport: Partial<SavedReportCompanyReport>) => void;
  updateValidation: (validation: SavedReportValidation<SavedReportCompanyReport>) => void;

  loadEvaluation: (accessType: number, favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReportCompanyReport>) => void;
  displayError: (errorMessage: string) => void;
  switchDisplayDisabled: (displayDisabled: boolean) => void;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  locale: string;
  savedReportsValidation: SavedReportsValidation<SavedReportCompanyReport>;
  translation: CompanyReportAdminTranslation;

  section: string;
}

export const CompanyReportSavedReports: React.FC<CompanyReportSavedReportsProps & CompanyReportSectionProps> = (props) => {
  const {
    savedReports, currentSavedReportId, currentSavedReport, currentSavedReportOnlyIdMode, currentValidation,
    addSavedReports, updateSavedReport, deleteSavedReport, deleteAllSavedReports, fetchingAddSavedReports,
    displayDisabled, switchDisplayDisabled, selectCurrent, updateCurrent, updateValidation,
    loadEvaluation, displayError, options, loadOptions, locale, savedReportsValidation, translation,
    section, getRef, shouldRenderSection
  } = props;

  const renderSavedReportForm = (savedReport: SavedReportCompanyReport) => <SavedReportForm
    savedReport={savedReport}
    updateCurrent={updateCurrent}
    endpoints={endpoints}
    options={options}
    loadOptions={loadOptions}
    locale={locale}
    validation={currentValidation}
    translation={translation} />;

  return <MetroLineSection<CompanyReportSectionType>
    section={section}
    sectionType={CompanyReportSectionType.SavedReports}
    getRef={getRef}
    shouldRenderSection={shouldRenderSection}>
    <div ref={getRef(CompanyReportSectionType.SavedReports)}>
      <SectionPanel
        className="admin-paper-top-panel"
        componentId={CompanyReportSectionType.SavedReports}
        validation={undefined}
        info={{ text: translation.saved_reports }}
        label={{ visible: true, text: translation.saved_reports }} />
      <CompanyReportDynamicPeriods {...props} />
      <SavedReports
        savedReports={savedReports}
        currentSavedReportId={currentSavedReportId}
        currentSavedReport={currentSavedReport}
        currentSavedReportOnlyIdMode={currentSavedReportOnlyIdMode}
        currentValidation={currentValidation}
        displayDisabled={displayDisabled}
        switchDisplayDisabled={switchDisplayDisabled}
        renderSavedReportForm={renderSavedReportForm}
        createDefaultSavedReport={createDefaultSavedReport}
        addSavedReports={addSavedReports}
        updateSavedReport={updateSavedReport}
        deleteSavedReport={deleteSavedReport}
        deleteAllSavedReports={deleteAllSavedReports}
        fetchingAddSavedReports={fetchingAddSavedReports}
        validateSavedReport={validateSavedReport}
        selectCurrent={selectCurrent}
        updateValidation={updateValidation}
        loadEvaluation={loadEvaluation}
        displayError={displayError}
        endpoints={endpoints}
        savedReportCategories={[SavedReportCategory.caseReport, SavedReportCategory.numeratorReport, SavedReportCategory.statisticalReport]}
        options={options}
        loadOptions={loadOptions}
        savedReportsValidation={savedReportsValidation}
        translation={translation}
        locale={locale}
        tileConfig={{
          add: false,
          edit: true,
          move: false,
          delete: true
        }} />
    </div>
  </MetroLineSection>;
};
